'use client'

import { cn } from '@/lib/utils'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from 'radix-ui'
import { useEffect, useRef } from 'react'

import { CloseIcon } from '../icons/CloseIcon'

interface IVideoDialogProps {
  open: boolean
  videoUrl: string
  onClose: () => void
}

export function VideoDialog({ open, videoUrl, onClose }: IVideoDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (open) {
      video.currentTime = 0
      video.play().catch(() => {})
    } else {
      video.pause()
      video.currentTime = 0
    }
  }, [open])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const video = videoRef.current
      if (!video) return

      if (e.code === 'Space') {
        const active = document.activeElement

        if (active && active.tagName === 'VIDEO') {
          e.stopPropagation()

          if (video.paused) {
            video.play()
          } else {
            video.pause()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [])

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'content fixed inset-0 z-50 bg-black/10 backdrop-blur-sm',
            'duration-300'
          )}
        />

        <Dialog.Content
          onOpenAutoFocus={(e) => {
            e.preventDefault() // stop Radix from focusing the close button
            videoRef.current?.focus()
          }}
          aria-describedby="Video"
          className={cn(
            'content',
            'fixed z-50',
            'inset-0 h-full w-full',
            'md:top-[50%] md:left-[50%] md:h-[90vh] md:w-[90vw]',
            'md:max-h-[75vh] md:max-w-[75vw]',
            'md:translate-x-[-50%] md:translate-y-[-50%]',
            'md.data-[state=closed]:zoom-out-95 md.data-[state=open]:zoom-in-95',
            'duration-300'
          )}
        >
          <VisuallyHidden.Root>
            <Dialog.Title>Video</Dialog.Title>
            <Dialog.Description>Video</Dialog.Description>
          </VisuallyHidden.Root>
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden md:rounded-lg">
            <Dialog.Close
              className={cn(
                'cursor-pointer',
                'absolute z-10',
                'top-4 right-4',
                'md:top-4 md:right-4 md:left-auto',
                'h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 lg:h-14 lg:w-14',
                'flex items-center justify-center text-white',
                'transition-colors duration-200',
                'focus:ring-2 focus:ring-white/20 focus:outline-none'
              )}
            >
              <CloseIcon size="24px" />
              <span className="sr-only">Close video</span>
            </Dialog.Close>

            {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- no caption track available for this source */}
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              controls
              playsInline
              autoPlay
              preload="auto"
              poster=""
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
